import React, {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { Route, Switch, withRouter, Redirect } from "react-router-dom";
import "./App.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "./store";
import { useHistory } from "react-router";
import AuthRoute from "./components/Routes/AuthRoute";
import { fetchOffers } from "./store/offers";
import ReactGa from "react-ga";
import { getQueryParameterByName } from "./utils";
import { skyscannerPixel } from "./tracking/skyscannerPixel";
import { possibleLanguages } from "./i18n";
import DocumentMeta from "./components/DocumentMeta/DocumentMeta";
import { Spin } from "antd";
import { generateMultilingualRoutes } from "./utils/generateRoutes";
import HeaderTop from "./components/Header/HeaderTop";
import "bootstrap/dist/css/bootstrap.min.css";
import AppBanner from "./components/AppBanner/AppBanner";
import appBannerIcon from "./assets/images/appBannerIcon.svg";

const Homepage = lazy(() => import("./containers/Home/Home"));
const BookFlight = lazy(() => import("./containers/BookFlight/BookFlight"));
const Bookings = lazy(() => import("./containers/Bookings/Bookings"));
const BookingDetails = lazy(
  () => import("./containers/BookingDetails/BookingDetails")
);
const About = lazy(() => import("./containers/About/About"));
const Contact = lazy(() => import("./containers/Contact/Contact"));
const Result = lazy(() => import("./containers/Result"));
const TermsAndConditions = lazy(
  () => import("./containers/Terms&Conditions/TermsAndConditions")
);
const PrivacyPolicy = lazy(
  () => import("./containers/PrivacyPolicy/PrivacyPolicy")
);
const Information = lazy(() => import("./containers/Information/Information"));
const CheckoutForm = lazy(() => import("./components/Stripe/Checkout"));
const MobileCheckout = lazy(() => import("./components/Stripe/MobileCheckout"));
const NotFound = lazy(() => import("./containers/NotFound/NotFound"));
const FindUserBookingNew = lazy(
  () => import("./containers/FindBooking/FindUserBookingNew")
);
const AgencyApplicationForm = lazy(
  () => import("./containers/AgencyApplicationForm/AgencyApplicationForm")
);
const InformationPage = lazy(
  () => import("./containers/TravelingWithChildren/InformationPage")
);
const FAQ = lazy(() => import("./containers/Faq/Faq"));
const Login = lazy(() => import("./containers/Auth/Login"));
const Register = lazy(() => import("./containers/Auth/Register"));
const ResetPassword = lazy(() => import("./containers/Auth/ResetPassword"));
const ForgotPassword = lazy(
  () => import("./containers/Auth/ForgotPassword")
);
const BabyBaggageAllowance = lazy(
  () => import("./containers/TravelingWithChildren/BabyBaggageAllowance")
);
const ChildTurnsTwo = lazy(
  () => import("./containers/TravelingWithChildren/ChildTurnsTwo")
);
const ChildTurnsTwelve = lazy(
  () => import("./containers/TravelingWithChildren/ChildTurnsTwelve")
);
const ChildrenAlone = lazy(
  () => import("./containers/TravelingWithChildren/ChildrenAlone")
);
const ChildrenBreastfeeding = lazy(
  () => import("./containers/TravelingWithChildren/ChildrenBreastfeeding")
);
const ChildrenStroller = lazy(
  () => import("./containers/TravelingWithChildren/ChildrenStroller")
);
const ChildrenCarSeat = lazy(
  () => import("./containers/TravelingWithChildren/ChildrenCarSeat")
);
const ChildrenUnborn = lazy(
  () => import("./containers/TravelingWithChildren/ChildrenUnborn")
);
const ChildrenPregnancy = lazy(
  () => import("./containers/TravelingWithChildren/ChildrenPregnancy")
);
const ChildrenSeating = lazy(
  () => import("./containers/TravelingWithChildren/ChildrenSeating")
);
const OnlineCheckInPage = lazy(
  () => import("./containers/Seats/OnlineCheckInPage")
);
const SpaceSeatInfoPage = lazy(
  () => import("./containers/Seats/SpaceSeatInfoPage")
);
const SeatSelectionGuidePage = lazy(
  () => import("./containers/Seats/SeatSelectionGuidePage")
);
const SeatReservationPage = lazy(
  () => import("./containers/Seats/SeatReservationPage")
);
const SeatbeltExtensionPage = lazy(
  () => import("./containers/Seats/SeatbeltExtensionPage")
);
const ChildrenSeatingPage = lazy(
  () => import("./containers/Seats/ChildrenSeatingPage")
);
const SupportIndex = lazy(
  () => import("./containers/RouteLogicForSupport/SupportIndex")
);
const BuyExtraSeatPage = lazy(
  () => import("./containers/Seats/BuyExtraSeatPage")
);
const BaggageCheckInPage = lazy(
  () => import("./containers/Baggage/BaggageCheckInPage")
);
const BaggageDamagedOrDelayedPage = lazy(
  () => import("./containers/Baggage/BaggageDamagedOrDelayedPage")
);
const BaggagePurchaseOnlinePage = lazy(
  () => import("./containers/Baggage/BaggagePurchaseOnlinePage")
);
const BaggageThroughCheckPage = lazy(
  () => import("./containers/Baggage/BaggageThroughCheckPage")
);
const BicyclePage = lazy(() => import("./containers/Baggage/BicyclePage"));
const CpapPocPage = lazy(() => import("./containers/Baggage/CpapPocPage"));
const DroneRulesPage = lazy(() => import("./containers/Baggage/DroneRulesPage"));
const HuntingGearPage = lazy(
  () => import("./containers/Baggage/HuntingGearPage")
);
const LiquidBanPage = lazy(() => import("./containers/Baggage/LiquidBanPage"));
const LostFoundPage = lazy(() => import("./containers/Baggage/LostFoundPage"));
const MusicInstrumentsPage = lazy(
  () => import("./containers/Baggage/MusicInstrumentsPage")
);
const OversizedPackingPage = lazy(
  () => import("./containers/Baggage/OversizedPackingPage")
);
const OxygenCanistersPage = lazy(
  () => import("./containers/Baggage/OxygenCanistersPage")
);
const ProhibitedItemsPage = lazy(
  () => import("./containers/Baggage/ProhibitedItemsPage")
);
const SpecialEquipmentCostPage = lazy(
  () => import("./containers/Baggage/SpecialEquipmentCostPage")
);
const SportsEquipmentPage = lazy(
  () => import("./containers/Baggage/SportsEquipmentPage")
);
const ChangeCancelBooking = lazy(
  () => import("./containers/InformationContent/ChangeCancelBooking")
);
const CheckinOnline = lazy(
  () => import("./containers/InformationContent/CheckinOnline")
);
const FlyingWithAnimalsPage = lazy(
  () => import("./containers/InformationContent/FlyingWithAnimalsPage")
);
const TravelDocumentsPage = lazy(
  () => import("./containers/InformationContent/TravelDocumentsPage")
);
const AllergiesSupportPage = lazy(
  () => import("./containers/SpecialAssistance/AllergiesSupportPage")
);
const CastTravelPage = lazy(
  () => import("./containers/SpecialAssistance/CastTravelPage")
);
const ChangePassengerPage = lazy(
  () => import("./containers/Booking/ChangePassenger")
);
const GroupBookingsPage = lazy(
  () => import("./containers/Booking/GroupBookings")
);
const HiddenDisabilityDPNAPage = lazy(
  () => import("./containers/SpecialAssistance/HiddenDisabilityDPNA")
);
const InvoiceRequestPage = lazy(
  () => import("./containers/Booking/InvoiceRequest")
);
const MedicationOnBoardPage = lazy(
  () => import("./containers/SpecialAssistance/MedicationOnBoard")
);
const MobilityAidsPage = lazy(
  () => import("./containers/SpecialAssistance/MobilityAidsPage")
);
const RefundApplyPage = lazy(
  () => import("./containers/Booking/RefundsApply")
);
const SafetyAssistantPage = lazy(
  () => import("./containers/SpecialAssistance/SafetyAssistant")
);
const SpecialAssistanceWheelchairPage = lazy(
  () => import("./containers/SpecialAssistance/Wheelchair")
);
const SpecialSeatingPage = lazy(
  () => import("./containers/SpecialAssistance/SpecialSeatingPage")
);
const RequestWheelchairPage = lazy(
  () => import("./containers/SpecialAssistance/RequestWheelchair")
);
const VerifyAccount = lazy(
  () => import("./containers/VerifyAccount/VerifyAccount")
);
const NavbarUser = lazy(() => import("./containers/NavbarUser/NavbarUser"));
const Return = lazy(() => import("./components/Stripe/Return"));

const RouteFallback = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    <Spin size="large" />
  </div>
);

// @ts-ignore
ReactGa.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS || "G-EWB4QQGBWX");

function App() {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const accessToken = useSelector((state: AppState) => state.auth.accessToken);
  const pricePreviewDetails = useSelector(
    (state: AppState) => state.selectedFlights.pricePreviewDetails
  );

  const language = getQueryParameterByName("language");
  const [loaderVisible, setLoaderVisible] = useState(false);
  const isMobileCheckoutHandoff =
    history.location.pathname.includes("/payment/mobile-checkout");

  const Loader = RouteFallback;

  useEffect(() => {
    ReactGa.pageview(window.location.pathname + window.location.search);
    //do not forget to add to the Home.tsx
    dispatch(fetchOffers());

    // if (window.location.pathname.startsWith('/booking/')) {
    //     setLoaderVisible(true);
    //     document.body.style.overflow = 'hidden';
    //     setTimeout(() => {
    //         const redirectUrl =
    //             process.env.NODE_ENV === 'development'
    //                 ? 'https://airtiketa.dev.bemyticket.com'
    //                 : 'https://airtiketa.eu';
    //         window.location.replace(redirectUrl);
    //     }, 1000);
    // }
    const getLanguage = sessionStorage.getItem("language");
    if (getLanguage) {
      i18n.changeLanguage(getLanguage);
      sessionStorage.setItem("language", getLanguage);
      sessionStorage.setItem("i18nextLng", getLanguage);
    } else {
      sessionStorage.setItem("language", i18n.language);
      sessionStorage.setItem("i18nextLng", i18n.language);
    }

    if (language && possibleLanguages.includes(language)) {
      onLanguageChange(language);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });

    // Skyscanner DV: re-capture (last-click-wins on in-app re-entry) then fire the funnel-stage
    // pixel for the current route. dayview/booking carry price+currency from the live preview, so
    // this also re-runs when pricePreviewDetails updates (price known after flight selection).
    // No-op for organic users.
    skyscannerPixel.captureFromUrl();
    skyscannerPixel.sendFunnelStage(history.location.pathname, pricePreviewDetails);
  }, [history.location.pathname, pricePreviewDetails]);

  useLayoutEffect(() => {
    const MOBILE_MAX_WIDTH = 768;
    const OFFSET_ADJUST_PX = 70;

    const applyOffset = () => {
      const isMobile = window.innerWidth <= MOBILE_MAX_WIDTH;

      const bannerDismissed =
        sessionStorage.getItem("airtiketa_app_banner_dismissed_v2") === "1";

      const bannerHeight =
        isMobile && !bannerDismissed
          ? document.getElementById("app-banner")?.offsetHeight ?? 0
          : 0;

      const headerHeight =
        document.getElementById("header-top-wrapper")?.offsetHeight ?? 0;

      const adjust = isMobile ? OFFSET_ADJUST_PX : 0;

      const total = Math.max(0, bannerHeight + headerHeight - adjust);

      document.documentElement.style.setProperty(
        "--app-top-offset",
        `${total}px`
      );
    };

    const sync = () => {
      requestAnimationFrame(() => {
        applyOffset();
        setTimeout(applyOffset, 0);
      });
    };

    sync();

    window.addEventListener("resize", sync, { passive: true });
    window.addEventListener("app-banner-changed", sync);

    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("app-banner-changed", sync);
    };
  }, []);

  const onLanguageChange = (lang: any) => {
    i18n.changeLanguage(lang);
    sessionStorage.setItem("language", lang);
    sessionStorage.setItem("i18nextLng", lang);
  };

  const componentMap = {
    InformationPage,
    BabyBaggageAllowance,
    ChildTurnsTwo,
    ChildTurnsTwelve,
    ChildrenAlone,
    ChildrenBreastfeeding,
    ChildrenStroller,
    ChildrenCarSeat,
    ChildrenUnborn,
    ChildrenPregnancy,
    ChildrenSeating,
    SpaceSeatInfoPage,
    SeatSelectionGuidePage,
    SeatReservationPage,
    SeatbeltExtensionPage,
    OnlineCheckInPage,
    ChildrenSeatingPage,
    BuyExtraSeatPage,
    BaggageCheckInPage,
    BaggageDamagedOrDelayedPage,
    BaggagePurchaseOnlinePage,
    BaggageThroughCheckPage,
    BicyclePage,
    CpapPocPage,
    DroneRulesPage,
    HuntingGearPage,
    LiquidBanPage,
    LostFoundPage,
    MusicInstrumentsPage,
    OversizedPackingPage,
    OxygenCanistersPage,
    ProhibitedItemsPage,
    SpecialEquipmentCostPage,
    SportsEquipmentPage,
    ChangeCancelBooking,
    CheckinOnline,
    FlyingWithAnimalsPage,
    TravelDocumentsPage,
    AllergiesSupportPage,
    CastTravelPage,
    ChangePassengerPage,
    GroupBookingsPage,
    HiddenDisabilityDPNAPage,
    InvoiceRequestPage,
    MedicationOnBoardPage,
    MobilityAidsPage,
    RefundApplyPage,
    SafetyAssistantPage,
    SpecialAssistanceWheelchairPage,
    SpecialSeatingPage,
    RequestWheelchairPage,
  };

  return (
    <div>
      {loaderVisible ? (
        <Loader />
      ) : (
        <div>
          <DocumentMeta />

          {!isMobileCheckoutHandoff && (
            <div id="app-banner-wrapper">
              <AppBanner
                iconSrc={appBannerIcon}
                androidUrl="https://play.google.com/store/apps/details?id=com.bmt.airtiketa&pcampaignid=web_share"
                iosUrl="https://apps.apple.com/xk/app/airtiketa/id6753178795"
              />
            </div>
          )}

          {!isMobileCheckoutHandoff && (
            <div id="header-top-wrapper">
              <HeaderTop />
            </div>
          )}

          <div className="app-content">
            {/*<AuthenticationDrawer isDrawerOpen={isDrawerOpen} onClose={() => {*/}
            {/*    dispatch(setLoginDrawer(false))*/}
            {/*    dispatch(setDrawerContent('login'));*/}

            {/*}} />*/}

            {/* <SmartBanner
            title="AirTiketa"
            author="AirTiketa Team"
            position="top"
            button="Install"
            storeText={{
              android: "Google Play",
              ios: "App Store",
            }}
            url={{
              android:
                "https://play.google.com/store/apps/details?id=com.airtiketa",
              ios: "https://apps.apple.com/xk/app/airtiketa/id6753178795",
            }}
            daysHidden={0}
            daysReminder={0}
          /> */}

            <Suspense fallback={<RouteFallback />}>
              <Switch>
                {generateMultilingualRoutes(componentMap)}
                <Route exact path="/" component={Homepage} />
                <Route path="/booking" component={BookFlight} />

                <Route exact path="/support" component={SupportIndex} />

                <Route path="/handlers/payments" component={Result} />
                <Route path="/find-booking" component={FindUserBookingNew} />

                <AuthRoute
                  exact
                  path="/bookings"
                  component={Bookings}
                  authenticated={!!accessToken}
                />
                <Route
                  path="/bookings/:reservationId"
                  component={BookingDetails}
                />
                <Route
                  path="/agency-registration"
                  component={AgencyApplicationForm}
                />

                <Route path="/about" component={About} />
                <Route path="/contact" component={Contact} />
                <Route path="/information" component={Information} />
                <Route
                  path="/terms&conditions"
                  component={TermsAndConditions}
                />

                <Route path="/privacy-policy" component={PrivacyPolicy} />
                <Route path="/payment/checkout" component={CheckoutForm} />
                <Route
                  path="/payment/mobile-checkout"
                  component={MobileCheckout}
                />
                <Route path="/stripe/return" component={Return} />
                <Route path="/faq" component={FAQ} />
                <Route path="/account-settings" component={NavbarUser} />
                {/* <Route path="/account-flights" component={UserFlights} />
              <Route path="/account" component={UserHome} /> */}
                {/* <Route path="/seat-selection" component={SeatMap} /> */}

                <Route
                  path="/login"
                  render={(props) =>
                    accessToken ? <Redirect to="/" /> : <Login />
                  }
                />
                <Route
                  path="/register"
                  render={(props) =>
                    accessToken ? <Redirect to="/" /> : <Register />
                  }
                />
                <Route
                  path="/reset-password"
                  render={(props) =>
                    accessToken ? <Redirect to="/" /> : <ResetPassword />
                  }
                />
                <Route
                  path="/forgot-password"
                  render={(props) =>
                    accessToken ? <Redirect to="/" /> : <ForgotPassword />
                  }
                />

                <Route path="/verify-email" component={VerifyAccount} />
                <Route path="*" component={NotFound} />
              </Switch>
            </Suspense>
          </div>
        </div>
      )}
    </div>
  );
}

export default withRouter(App);
